<template>
  <div class="gs-app gs-promo-tools gs-affiliate-portal">
    <v-wait for="tools">
      <div slot="waiting">
        <div class="gs-promo-tools__waiting">
          {{ $t('frontend_resource_promo_connecting', {appTitle: whitelabelAppData('sell').title}) }}
        </div>
      </div>

      <section v-if="portalState === 'not-live'" class="gs-promo-tools__unavailable" role="status">
        <span class="gs-promo-tools__availability-icon" aria-hidden="true">◷</span>
        <h1>This funnel is not live. Check back soon.</h1>
      </section>
      <section v-else-if="portalState === 'unavailable' || portalState === 'error'" class="gs-promo-tools__unavailable" role="status">
        <h1>{{ portalState === 'unavailable' ? 'This affiliate portal is unavailable.' : 'We couldn’t load this affiliate portal.' }}</h1>
        <p>Please check back soon.</p>
        <button v-if="portalState === 'error'" type="button" @click="loadPortal">Try again</button>
      </section>
      <div v-else-if="portalState === 'ready' && (storeUser || isUserLoggedinChecker)">
        <promo-tools-post-login
          :funnel-id="funnelId"
          @userLoggedOut="userLoggedOut"
        />
      </div>

      <div v-else-if="portalState === 'ready'" class="gs-promo-tools__auth">
        <login-signup-tabs
          :funnel-id="funnelId"
          :signup-form="signupForm"
          :header-text="headerText"
          :login-tabs="loginTabs"
          @userLoggedIn="userLoggedIn"
        />
      </div>

      <div
        v-if="!disableBranding"
        class="gs-promo-tools__brand"
      >
        {{ $t('frontend_resource_promo_poweredby') }}
        <span class="gs-promo-tools__brand-name">{{ this.brandName }}</span>
      </div>
    </v-wait>
  </div>
</template>

<script>
import axios from 'axios';
import auth from '@groovepages/gd-lib/auth';
import PromoToolsPostLogin from './promo_tools_post_login';
import LoginSignupTabs from './promo_tools_partials/LoginSignupTabs.vue';
import keycloakService from '@/services/keycloakAuth';
import { getEmbeddedUser, clearEmbeddedUser } from '@/services/portal-embed-session';

export default {
  layout: 'iframe',
  metaInfo() {
    return { title: this.$t('frontend_promo_tools') };
  },
  components: {
    PromoToolsPostLogin,
    LoginSignupTabs
  },
  data() {
    return {
      portalState: 'loading',
      funnelId: 0,
      disableBranding: false,
      signupForm: {
        first_name: '',
        last_name: '',
        company_name: '',
        email: '',
        username: '',
        password: '',
        password_confirmation: '',
        funnel_id: 0,
      },
      isUserLoggedinChecker: 0,
      isLoggedOut: 0,
      keycloakChecked: false,
    };
  },
  computed: {
    brandName() {
      return this.$store.state.brandData.brandName;
    },
    headerText: {
      get() {
        return this.$t('frontend_resource_promo_login_to', {appTitle: this.whitelabelAppData('sell').title});
      },
      set() {},
    },
    loginTabs: {
      get() {
        return [
          {
            title: this.$t('frontend_resource_promo_login_to', {appTitle: this.whitelabelAppData('sell').title}),
            value: 'login'
          },
          {
            title: this.$t('frontend_resource_promo_create_your', {appTitle: this.whitelabelAppData('sell').title}),
            value: 'signup',
          },
        ];
      },
      set() {},
    },
    storeUser() {
      if (this.isLoggedOut) {
        return null;
      }
      if (window.self !== window.top) return getEmbeddedUser();
      return auth.authenticatedUser('promo_tools');
    },
  },
  watch: {
    storeUser(newStoreUser) {
      if (newStoreUser) {
      } else {
        this.isUserLoggedinChecker = 0;
      }
    },
  },
  created() {
    window.addEventListener('groove-portal-session-expired', this.userLoggedOut);
    this.loadPortal();
  },
  beforeDestroy() {
    window.removeEventListener('groove-portal-session-expired', this.userLoggedOut);
  },
  methods: {
    async loadPortal() {
      this.portalState = 'loading';
      this.$wait.start('tools');
      try {
        const entryUrl = new URL(window.location.href);
        const startLogin = entryUrl.searchParams.get('portal_login') === '1';
        if (startLogin && window.self === window.top) {
          entryUrl.searchParams.delete('portal_login');
          window.history.replaceState(window.history.state, '', entryUrl.href);
        }
        // Always consume a top-level OAuth callback before any authenticated API
        // request, even if a legacy user is still stored from an earlier login.
        const ssoAuthenticated = await keycloakService.init();
        const { data } = await axios.get(`get-promo-tools-funnel-id/${this.$route.params.resource_id}`);
        if (data.status !== 'success' || !data.funnel_id) {
          this.portalState = 'unavailable';
          return;
        }
        this.disableBranding = data.disable_branding;
        if (data.is_live === false || data.is_live === 0 || data.is_live === '0') {
          this.portalState = 'not-live';
          return;
        }
        this.funnelId = data.funnel_id;
        this.signupForm.funnel_id = data.funnel_id;
        this.headerText = data.header_text;
        this.loginTabs[0].title = data.login_text;
        this.loginTabs[1].title = data.signup_text;
        if (!ssoAuthenticated && startLogin && window.self === window.top) {
          await keycloakService.login();
        } else if (ssoAuthenticated || getEmbeddedUser() || (window.self === window.top && auth.authenticatedUser())) {
          this.userLoggedIn();
        }
        this.keycloakChecked = true;
        this.portalState = 'ready';
      } catch (error) {
        this.portalState = error.response && error.response.status === 404 ? 'unavailable' : 'error';
      } finally {
        this.$wait.end('tools');
      }
    },
    userLoggedOut() {
      clearEmbeddedUser();
      this.isUserLoggedinChecker = 0;
      this.isLoggedOut = 1;
      auth.logout();
    },
    userLoggedIn() {
      this.isUserLoggedinChecker = 1;
      this.isLoggedOut = 0;
    },
  },
};
</script>

<style scoped>
/* No viewport-height rules: the hosted page is iframed and sized by
   iframeResizer from the document's natural height. */
.gs-promo-tools {
  min-width: 0;
  color: var(--gs-foreground);
  background: var(--gs-background);
}

.gs-promo-tools__unavailable { margin:24px auto; padding:36px 24px; max-width:600px; text-align:center; border:1px solid #E8DCC9; border-radius:14px; background:#FBF7EF; color:#593E32; }
.gs-promo-tools__unavailable h1 { margin:14px 0 0; font-size:22px; line-height:1.45; font-weight:700; }
.gs-promo-tools__unavailable p { margin-top:12px; }
.gs-promo-tools__availability-icon { font-size:36px; color:#966747; }
.gs-promo-tools__unavailable button { margin-top:18px; border:1px solid currentColor; border-radius:8px; padding:10px 16px; background:white; }
.gs-promo-tools__waiting {
  padding: 32px 20px;
  color: var(--gs-muted-foreground);
  font-size: 15px;
  font-weight: 600;
  text-align: center;
}

.gs-promo-tools__auth {
  display: flex;
  flex-direction: column;
}

.gs-promo-tools__brand {
  display: flex;
  gap: 4px;
  justify-content: flex-end;
  padding: 0 20px 16px;
  color: var(--gs-muted-foreground);
  font-size: 12px;
}

.gs-promo-tools__brand-name {
  font-weight: 700;
}

@media (max-width: 640px) {
  .gs-promo-tools__brand {
    padding: 0 12px 12px;
  }
}
</style>
